/* Boho V2: open Boulevard's official booking overlay from existing booking URLs.
 * The real href remains available if JavaScript or Boulevard cannot load.
 */
(() => {
  const config = document.querySelector('[data-boho-boulevard-config]');
  if (!config || window.bohoBoulevardBookingInstalled) return;
  window.bohoBoulevardBookingInstalled = true;

  const businessId = config.dataset.businessId;
  const locationId = config.dataset.locationId;
  const bookingPath = `/booking/businesses/${businessId}/widget`;
  const TRACKING_STORAGE_KEY = 'boho_blvd_tracking';
  const TRACKED_QUERY_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'referral_program_offer_code',
    'gclid',
    'fbclid',
    'wbraid',
    'gbraid',
    'msclkid',
    'google_client_id',
    'google_session_id',
    'fbp',
    'fbc',
  ];
  let loadPromise;
  let opening = false;
  const mobileDialog = document.querySelector('[data-boho-booking-dialog]');
  let returnFocus;
  mobileDialog?.addEventListener('close', () => {
    mobileDialog.querySelector('iframe')?.remove();
    returnFocus?.focus();
  });
  mobileDialog?.querySelector('[data-boho-booking-close]')?.addEventListener('click', () => mobileDialog.close());

  function readCookie(name) {
    try {
      const parts = (`; ${document.cookie || ''}`).split(`; ${name}=`);
      if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift().trim());
    } catch {}
    return '';
  }

  function parseGaClientId(raw) {
    if (!raw) return '';
    const stripped = raw.replace(/^GA\d+\.\d+\./, '');
    return stripped && stripped !== raw ? stripped : raw;
  }

  function parseGaSessionId(raw) {
    if (!raw) return '';
    const gs2 = raw.match(/(?:^|\.)s(\d+)(?:\$|\.|$)/);
    if (gs2) return gs2[1];
    const parts = raw.split('.');
    return parts.length >= 3 && /^\d+$/.test(parts[2]) ? parts[2] : '';
  }

  function findGaSessionCookie() {
    const preferred = readCookie('_ga_7D4R0VZZVX');
    if (preferred) return preferred;
    try {
      for (const chunk of (document.cookie || '').split(';')) {
        const [rawName, ...rest] = chunk.trim().split('=');
        if (rawName && rawName.startsWith('_ga_') && rest.length) {
          return decodeURIComponent(rest.join('=').trim());
        }
      }
    } catch {}
    return '';
  }

  function parseGclAw(raw) {
    if (!raw) return '';
    const match = raw.match(/^GCL\.\d+\.(.+)$/);
    return match ? match[1] : '';
  }

  function persistLandingParams() {
    const incoming = {};
    try {
      const search = new URLSearchParams(location.search);
      for (const key of TRACKED_QUERY_KEYS) {
        const val = search.get(key);
        if (val) incoming[key] = val;
      }
    } catch {}
    let stored = {};
    try {
      stored = JSON.parse(sessionStorage.getItem(TRACKING_STORAGE_KEY) || '{}') || {};
    } catch {}
    const merged = { ...stored, ...incoming };
    if (Object.keys(incoming).length > 0) {
      try {
        sessionStorage.setItem(TRACKING_STORAGE_KEY, JSON.stringify(merged));
      } catch {}
    }
    return merged;
  }

  function collectTrackingParams() {
    const tracked = { ...persistLandingParams() };
    if (!tracked.gclid) {
      const gclCookie = parseGclAw(readCookie('_gcl_aw'));
      if (gclCookie) tracked.gclid = gclCookie;
    }
    if (!tracked.google_client_id) {
      const cid = parseGaClientId(readCookie('_ga'));
      if (cid) tracked.google_client_id = cid;
    }
    if (!tracked.google_session_id) {
      const sid = parseGaSessionId(findGaSessionCookie());
      if (sid) tracked.google_session_id = sid;
    }
    if (!tracked.fbp) {
      const fbp = readCookie('_fbp');
      if (fbp) tracked.fbp = fbp;
    }
    if (!tracked.fbc) {
      const fbc = readCookie('_fbc');
      if (fbc) tracked.fbc = fbc;
      else if (tracked.fbclid) tracked.fbc = `fb.1.${Date.now()}.${tracked.fbclid}`;
    }
    return tracked;
  }

  function buildWidgetUrl(params, includeInjectorVersion = false) {
    const url = new URL(`https://dashboard.boulevard.io${bookingPath}`);
    const merged = { ...collectTrackingParams(), ...params };
    for (const [key, value] of Object.entries(merged)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    }
    if (includeInjectorVersion) url.searchParams.set('injector-version', '1.0');
    return url;
  }

  function buildFallbackHref(anchorHref, params) {
    const tracking = collectTrackingParams();
    try {
      const parsed = new URL(anchorHref, location.href);
      if (parsed.origin === location.origin && parsed.hash === '#book-now') {
        return buildWidgetUrl(params, false).href;
      }
      for (const [key, value] of Object.entries({ ...tracking, ...params })) {
        if (value !== undefined && value !== null && value !== '' && !parsed.searchParams.has(key)) {
          parsed.searchParams.set(key, String(value));
        }
      }
      return parsed.href;
    } catch {
      return buildWidgetUrl(params, false).href;
    }
  }

  persistLandingParams();

  function openMobile(params, anchor) {
    const iframe = document.createElement('iframe');
    const url = buildWidgetUrl(params, true);
    iframe.src = url.href;
    iframe.title = 'Boho Med Spa secure appointment booking';
    iframe.setAttribute('sandbox', 'allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox');
    // The official injector sends this same open signal after iframe load.
    iframe.addEventListener('load', () => iframe.contentWindow?.postMessage('open', 'https://www.joinblvd.com'));
    mobileDialog.querySelector('iframe')?.remove();
    mobileDialog.appendChild(iframe);
    const fallbackEl = mobileDialog.querySelector('[data-boho-booking-fallback]');
    if (fallbackEl) fallbackEl.href = buildFallbackHref(anchor.href, params);
    returnFocus = anchor.closest('#boho-nav-drawer') ? document.querySelector('#boho-header-menu-button') : anchor;
    mobileDialog.showModal();
  }

  function destination(anchor) {
    if (anchor.hasAttribute('download')) return null;
    let url;
    try { url = new URL(anchor.href, location.href); } catch { return null; }
    if (url.hostname === 'dashboard.boulevard.io' && url.pathname === bookingPath) {
      const hashPath = url.hash.startsWith('#/') ? url.hash.slice(1) : '';
      return {
        locationId: url.searchParams.get('locationId') || locationId,
        path: url.searchParams.get('path') || (hashPath && hashPath !== '/' ? hashPath : '/cart/menu'),
        visitType: url.searchParams.get('visitType') || 'SELF_VISIT',
      };
    }
    if ((url.hostname === 'blvd.me' && /^\/bohomedspa\/?$/.test(url.pathname)) ||
        (url.origin === location.origin && url.pathname === location.pathname && url.hash === '#book-now')) {
      return { locationId, path: '/cart/menu', visitType: 'SELF_VISIT' };
    }
    return null;
  }

  function loadBoulevard() {
    if (loadPromise) return loadPromise;
    loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const timer = setTimeout(() => fail(new Error('Booking script timed out')), 12000);
      function fail(error) {
        clearTimeout(timer);
        script.remove();
        loadPromise = undefined;
        reject(error);
      }
      script.src = 'https://static.joinboulevard.com/injector.min.js';
      script.async = true;
      script.onload = () => {
        clearTimeout(timer);
        try {
          if (!window.blvd) throw new Error('Booking script unavailable');
          window.blvd.init({ businessId });
          // Initialization may wait for analytics callbacks. Do not open early.
          const readyDeadline = Date.now() + 6000;
          const ready = () => {
            if (document.querySelector('iframe.blvd-booking-widget')) resolve(window.blvd);
            else if (Date.now() > readyDeadline) fail(new Error('Booking initialization timed out'));
            else setTimeout(ready, 50);
          };
          ready();
        } catch (error) { fail(error); }
      };
      script.onerror = () => fail(new Error('Booking script unavailable'));
      document.head.appendChild(script);
    });
    return loadPromise;
  }

  document.addEventListener('click', async (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target.closest?.('a[href]');
    if (!anchor) return;
    const params = destination(anchor);
    if (!params) return;
    event.preventDefault();
    if (opening) return;
    opening = true;
    anchor.setAttribute('aria-busy', 'true');
    config.textContent = 'Opening booking…';
    try {
      // Release the site's native modal before opening the provider iframe.
      const dialogComponent = anchor.closest('dialog-component');
      if (dialogComponent?.querySelector('dialog[open]')) {
        if (typeof dialogComponent.closeDialog === 'function') await dialogComponent.closeDialog();
        else dialogComponent.querySelector('dialog[open]').close();
      }
      const drawer = anchor.closest('header-drawer');
      if (drawer?.isOpen && typeof drawer.close === 'function') drawer.close();
      if (mobileDialog && (innerWidth <= 800 || /iPhone|iPod|iPad|Android|Mobile/i.test(navigator.userAgent))) {
        openMobile(params, anchor);
      } else {
        const boulevard = await loadBoulevard();
        boulevard.openBookingWidget({ urlParams: { ...collectTrackingParams(), ...params } });
      }
      config.textContent = '';
    } catch {
      config.textContent = 'Opening the secure booking page…';
      location.assign(buildFallbackHref(anchor.href, params));
    } finally {
      opening = false;
      anchor.removeAttribute('aria-busy');
    }
  });
})();
/* Boho Book-tap credit (T-0070, 26 Oct 2026, Jorge's pick): Boulevard cannot tell Google Ads which
   ad click booked, so a tap on any Book button counts as the Ads conversion "Book tap", sent from
   this browser so the ad click comes with it. Once per page session; no client data. */
(()=>{if(window.bohoBookTapInstalled)return;window.bohoBookTapInstalled=!0;
const ADS_ID="AW-17888976567",SEND_TO="AW-17888976567/rA6CCPLb8-McELe9kNJC",SENT_KEY="boho_book_tap_sent";
window.dataLayer=window.dataLayer||[];window.gtag=window.gtag||function(){dataLayer.push(arguments)};
if(!document.querySelector(`script[src*="gtag/js?id=${ADS_ID}"]`)){const s=document.createElement("script");s.async=!0;s.src=`https://www.googletagmanager.com/gtag/js?id=${ADS_ID}`;document.head.appendChild(s)}
gtag("js",new Date);gtag("config",ADS_ID,{send_page_view:!1});
function isBook(anchor){let u;try{u=new URL(anchor.href,location.href)}catch{return!1}
 return/(^|\.)(boulevard\.io|joinblvd\.com|blvd\.me)$/.test(u.hostname)||(u.origin===location.origin&&u.hash==="#book-now")}
document.addEventListener("click",e=>{const a=e.target.closest?.("a[href]");if(!a||!isBook(a))return;
 try{if(sessionStorage.getItem(SENT_KEY))return;sessionStorage.setItem(SENT_KEY,"1")}catch{}
 gtag("event","conversion",{send_to:SEND_TO,transport_type:"beacon"});
 dataLayer.push({event:"boho_book_tap",book_path:innerWidth<=800?"phone":"desktop"})},!0)})();
